import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DebugUiBridge } from '../DebugUiBridge';
import type { BehaviorEntityInfo, BehaviorParamValue } from '../types';

/**
 * Behavior Editor Panel
 * 
 * Edit behavior parameters for entities in real-time.
 * Changes are applied immediately for visual feedback.
 */
@customElement('behavior-editor-panel')
export class BehaviorEditorPanel extends LitElement {
  @state() private entities: BehaviorEntityInfo[] = [];
  @state() private expandedEntityId: string | null = null;
  @state() private filterText = '';
  @state() private filterBehavior = '';
  
  private pollIntervalId: number | null = null;
  private static readonly POLL_INTERVAL_MS = 2000; // Poll every 2s for dynamic changes
  
  static styles = css`
    :host {
      display: block;
      color: white;
      font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 13px;
      padding: 12px;
      overflow-y: auto;
      height: 100%;
      box-sizing: border-box;
    }
    
    .toolbar {
      display: flex;
      gap: 8px;
      margin-bottom: 12px;
      flex-wrap: wrap;
    }
    
    .toolbar input, .toolbar select {
      flex: 1;
      min-width: 100px;
      padding: 6px 10px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.3);
      color: white;
      font-size: 12px;
    }
    
    .toolbar button {
      padding: 6px 12px;
      border: none;
      border-radius: 4px;
      background: rgba(100, 150, 255, 0.3);
      color: white;
      cursor: pointer;
      font-size: 12px;
    }
    
    .toolbar button:hover {
      background: rgba(100, 150, 255, 0.5);
    }
    
    .entity-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    
    .entity-card {
      background: rgba(0, 0, 0, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 6px;
      overflow: hidden;
    }
    
    .entity-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 12px;
      cursor: pointer;
      background: rgba(255, 255, 255, 0.05);
    }
    
    .entity-header:hover {
      background: rgba(255, 255, 255, 0.1);
    }
    
    .entity-header .expand-icon {
      opacity: 0.6;
      font-size: 10px;
      transition: transform 0.2s;
    }
    
    .entity-header.expanded .expand-icon {
      transform: rotate(90deg);
    }
    
    .entity-name {
      flex: 1;
      font-weight: 500;
      font-size: 13px;
    }
    
    .entity-behavior {
      font-size: 11px;
      padding: 2px 8px;
      background: rgba(100, 150, 255, 0.2);
      border-radius: 10px;
      opacity: 0.8;
    }
    
    .entity-params {
      padding: 12px;
      display: flex;
      flex-direction: column;
      gap: 6px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .param-group {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid rgba(255, 255, 255, 0.05);
    }
    
    .param-group-title {
      font-size: 11px;
      font-weight: 600;
      opacity: 0.7;
      margin-bottom: 8px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    .param-row {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    
    .param-label {
      min-width: 140px;
      font-size: 12px;
      opacity: 0.85;
    }
    
    .param-label .description {
      font-size: 10px;
      opacity: 0.5;
      display: block;
      margin-top: 2px;
    }
    
    .param-input {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .param-input input[type="number"],
    .param-input input[type="text"],
    .param-input select {
      flex: 1;
      padding: 5px 8px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.4);
      color: white;
      font-size: 12px;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    }
    
    .param-input input[type="range"] {
      flex: 1;
      min-width: 100px;
    }
    
    .param-input .value-display {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 11px;
      min-width: 50px;
      text-align: right;
      opacity: 0.8;
    }
    
    .param-input input[type="checkbox"] {
      width: 16px;
      height: 16px;
    }
    
    .color-input-group {
      display: flex;
      gap: 4px;
      flex: 1;
    }
    
    .color-input-group input {
      width: 60px;
      text-align: center;
    }
    
    .color-input-group .color-label {
      font-size: 10px;
      opacity: 0.6;
      text-align: center;
      margin-top: 2px;
    }
    
    .color-preview {
      width: 24px;
      height: 24px;
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 4px;
    }
    
    .empty-state {
      text-align: center;
      padding: 40px 20px;
      opacity: 0.6;
    }
    
    .empty-state .icon {
      font-size: 48px;
      margin-bottom: 12px;
    }
  `;
  
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('himum:debug-ui-event', this.onDebugEvent as EventListener);
    
    // Request initial behavior list
    this.refresh();
    
    // Start polling for dynamic changes (particle systems created/destroyed at runtime)
    this.pollIntervalId = window.setInterval(() => this.refresh(), BehaviorEditorPanel.POLL_INTERVAL_MS);
  }
  
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('himum:debug-ui-event', this.onDebugEvent as EventListener);
    
    // Stop polling
    if (this.pollIntervalId !== null) {
      window.clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
  }
  
  private onDebugEvent = (e: CustomEvent) => {
    const event = e.detail;
    
    if (event.type === 'behaviors:list') {
      this.entities = event.entities;
    }
    
    // Auto-refresh when scene graph changes (map loaded, entities spawned)
    if (event.type === 'scene:graph') {
      this.refresh();
    }
  };
  
  private refresh() {
    DebugUiBridge.dispatchCommand({ type: 'behaviors:refresh' } as any);
  }
  
  private toggleEntity(entityId: string) {
    this.expandedEntityId = this.expandedEntityId === entityId ? null : entityId;
  }
  
  private onParamChange(entityId: string, paramName: string, value: number | string | boolean, paramDef: BehaviorParamValue) {
    // Convert value to correct type
    let typedValue: number | string | boolean = value;
    if (paramDef.type === 'number') {
      typedValue = parseFloat(value as string);
      if (isNaN(typedValue)) return;
    } else if (paramDef.type === 'boolean') {
      typedValue = Boolean(value);
    }
    
    // Dispatch update command
    DebugUiBridge.dispatchCommand({
      type: 'behaviors:update',
      entityId,
      paramName,
      value: typedValue
    } as any);
    
    // Update local state for immediate feedback
    const entity = this.entities.find(e => e.id === entityId);
    if (entity && entity.parameters[paramName]) {
      entity.parameters[paramName].value = typedValue;
      this.requestUpdate();
    }
  }
  
  private getFilteredEntities(): BehaviorEntityInfo[] {
    let filtered = this.entities;
    
    if (this.filterText) {
      const search = this.filterText.toLowerCase();
      filtered = filtered.filter(e => 
        e.id.toLowerCase().includes(search) ||
        e.displayName.toLowerCase().includes(search)
      );
    }
    
    if (this.filterBehavior) {
      filtered = filtered.filter(e => e.behaviorId === this.filterBehavior);
    }
    
    return filtered;
  }
  
  private getUniqueBehaviors(): string[] {
    const behaviors = new Set(this.entities.map(e => e.behaviorId));
    return Array.from(behaviors).sort();
  }
  
  private groupParameters(params: Record<string, BehaviorParamValue>): Map<string, [string, BehaviorParamValue][]> {
    const groups = new Map<string, [string, BehaviorParamValue][]>();
    
    for (const [name, param] of Object.entries(params)) {
      const group = param.group || 'General';
      if (!groups.has(group)) {
        groups.set(group, []);
      }
      groups.get(group)!.push([name, param]);
    }
    
    return groups;
  }
  
  private formatParamName(name: string): string {
    // Convert camelCase to Title Case with spaces
    return name
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }
  
  private renderParam(entityId: string, paramName: string, param: BehaviorParamValue) {
    const formattedName = this.formatParamName(paramName);
    
    return html`
      <div class="param-row">
        <div class="param-label">
          ${formattedName}
          ${param.description ? html`<span class="description">${param.description}</span>` : nothing}
        </div>
        <div class="param-input">
          ${this.renderParamInput(entityId, paramName, param)}
        </div>
      </div>
    `;
  }
  
  private renderParamInput(entityId: string, paramName: string, param: BehaviorParamValue) {
    switch (param.type) {
      case 'boolean':
        return html`
          <input 
            type="checkbox" 
            .checked=${param.value as boolean}
            @change=${(e: Event) => this.onParamChange(entityId, paramName, (e.target as HTMLInputElement).checked, param)}
          />
        `;
        
      case 'enum':
        return html`
          <select
            @change=${(e: Event) => this.onParamChange(entityId, paramName, (e.target as HTMLSelectElement).value, param)}
          >
            ${param.enumValues?.map(opt => html`
              <option value=${opt} ?selected=${param.value === opt}>${opt}</option>
            `)}
          </select>
        `;
        
      case 'number':
        const step = param.step ?? 0.1;
        const min = param.min ?? 0;
        const max = param.max ?? 100;
        const value = param.value as number;
        
        return html`
          <input 
            type="range" 
            .value=${String(value)}
            min=${min}
            max=${max}
            step=${step}
            @input=${(e: Event) => this.onParamChange(entityId, paramName, (e.target as HTMLInputElement).value, param)}
          />
          <input 
            type="number" 
            .value=${String(value)}
            min=${min}
            max=${max}
            step=${step}
            style="width: 70px; flex: none;"
            @change=${(e: Event) => this.onParamChange(entityId, paramName, (e.target as HTMLInputElement).value, param)}
          />
        `;
        
      case 'string':
      default:
        return html`
          <input 
            type="text" 
            .value=${String(param.value)}
            @change=${(e: Event) => this.onParamChange(entityId, paramName, (e.target as HTMLInputElement).value, param)}
          />
        `;
    }
  }
  
  private renderEntity(entity: BehaviorEntityInfo) {
    const isExpanded = this.expandedEntityId === entity.id;
    const paramGroups = this.groupParameters(entity.parameters);
    
    return html`
      <div class="entity-card">
        <div 
          class="entity-header ${isExpanded ? 'expanded' : ''}"
          @click=${() => this.toggleEntity(entity.id)}
        >
          <span class="expand-icon">▶</span>
          <span class="entity-name">${entity.id}</span>
          <span class="entity-behavior">${entity.displayName}</span>
        </div>
        ${isExpanded ? html`
          <div class="entity-params">
            ${Array.from(paramGroups.entries()).map(([groupName, params], i) => html`
              <div class="${i > 0 ? 'param-group' : ''}">
                ${paramGroups.size > 1 ? html`
                  <div class="param-group-title">${groupName}</div>
                ` : nothing}
                ${params.map(([name, param]) => this.renderParam(entity.id, name, param))}
              </div>
            `)}
          </div>
        ` : nothing}
      </div>
    `;
  }
  
  render() {
    const filteredEntities = this.getFilteredEntities();
    const uniqueBehaviors = this.getUniqueBehaviors();
    
    return html`
      <div class="toolbar">
        <input 
          type="text" 
          placeholder="Search entities..."
          .value=${this.filterText}
          @input=${(e: Event) => this.filterText = (e.target as HTMLInputElement).value}
        />
        <select @change=${(e: Event) => this.filterBehavior = (e.target as HTMLSelectElement).value}>
          <option value="">All behaviors</option>
          ${uniqueBehaviors.map(b => html`
            <option value=${b}>${b}</option>
          `)}
        </select>
        <button @click=${this.refresh}>🔄 Refresh</button>
      </div>
      
      ${filteredEntities.length === 0 ? html`
        <div class="empty-state">
          <div class="icon">📦</div>
          <div>No behavior entities found</div>
          <div style="font-size: 11px; margin-top: 8px;">
            Load a map with particle emitters, triggers, or other behaviors
          </div>
        </div>
      ` : html`
        <div class="entity-list">
          ${filteredEntities.map(entity => this.renderEntity(entity))}
        </div>
      `}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'behavior-editor-panel': BehaviorEditorPanel;
  }
}
